<template>
  <button
    type="button"
    class="help-nav"
    data-tour="help-nav"
    aria-label="Abrir ayuda"
    @click="openHelpChat"
  >
    <v-badge :model-value="showBadge" content="!" color="error">
      <v-icon size="22">mdi-lifebuoy</v-icon>
    </v-badge>
    <span class="help-nav__label">Ayuda</span>
  </button>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import { storeToRefs } from 'pinia'
import { useGroupChatsStore } from '@/stores/groupChats'
import { useHelpChatStore } from '@/stores/helpChat'
import { useNotificationsStore } from '@/stores/notifications'

export default defineComponent({
  name: 'HelpChatLauncher',

  props: {
    variant: {
      type: String as PropType<'fab' | 'nav'>,
      required: true,
    },
  },

  setup() {
    const helpChatStore = useHelpChatStore()
    const groupChatsStore = useGroupChatsStore()
    const notificationsStore = useNotificationsStore()
    const { hasBeenOpened } = storeToRefs(helpChatStore)
    const showBadge = computed(() => !hasBeenOpened.value)

    const openHelpChat = () => {
      groupChatsStore.close()
      notificationsStore.closePreview()
      helpChatStore.open()
    }

    return {
      showBadge,
      openHelpChat,
    }
  },
})
</script>

<style scoped>
.help-fab {
  position: fixed;
  right: 20px;
  bottom: 20px;
  /* Below right panels (1000) and dialogs */
  z-index: 900;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: inherit;
}

.help-fab__circle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  box-shadow:
    0 8px 22px rgba(0, 0, 0, 0.28),
    0 24px 64px rgba(0, 0, 0, 0.38);
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.help-fab:hover .help-fab__circle {
  transform: translateY(-4px);
  box-shadow:
    0 14px 28px rgba(0, 0, 0, 0.3),
    0 32px 80px rgba(0, 0, 0, 0.42);
}

.help-fab__label {
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  color: rgb(var(--v-theme-on-surface));
  text-shadow:
    0 0 4px rgb(var(--v-theme-surface)),
    0 1px 2px rgb(var(--v-theme-surface));
}

.help-nav {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  border: 0;
  background: transparent;
  color: white;
  cursor: pointer;
  width: auto;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  gap: 6px;
}

.help-nav:hover {
  background: rgba(255, 255, 255, 0.08);
}

.help-nav__label {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
}

.help-fab__circle .help-launcher__badge {
  top: -4px;
  right: -4px;
}

.help-nav .help-launcher__badge {
  top: 2px;
  right: 2px;
}

.help-launcher__badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: rgb(var(--v-theme-error));
  color: rgb(var(--v-theme-on-error));
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}
</style>
